import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { Button } from 'shared/ui'
import { Table } from 'shared/ui/TableNew'
import { uiStore } from 'shared/store/uiStore'
import { type TeamRow } from 'entities/Teams'
import { useTeamsContext } from 'pages/settings/pages/teams/context/teamsContext'
import { TeamsPath } from 'pages/settings/pages/teams/route/teamsPath'
import { getTeamsTableActions } from 'pages/settings/pages/teams/ui/TeamsListTable/getTeamsTableActions'
import styles from './styles.module.scss'
import { columns } from './columns'

export const TeamsListTable = observer(() => {
  const { pathname } = useLocation()
  const teamsStore = useTeamsContext()
  const { tableStore, loading, itemsRows } = teamsStore
  const selectedIds = tableStore.bulkStore.selectedIds as number[]

  const selectedTeamIdsForResync = useMemo(
    () => selectedIds.filter((id) => teamsStore.getItem(id)?.isIntegrationTeam),
    [selectedIds, teamsStore]
  )
  const hasSelectedIntegrationTeam = selectedTeamIdsForResync.length > 0

  const getColumns = useMemo(() => columns(), [])

  const rows = useMemo(() => {
    return itemsRows.map((item) => {
      item.setActionsProps({
        actions: getTeamsTableActions(item, teamsStore),
      })

      return item
    })
  }, [itemsRows, teamsStore])

  useEffect(() => {
    if (pathname === TeamsPath.root) {
      teamsStore.resetAllTableSelections()
    }
  }, [pathname, teamsStore])

  const onRowClick = (row: TeamRow) => {
    teamsStore.resetAllTableSelections()
    uiStore.changeRoute({ path: TeamsPath.getManage(row.origin.id) })
  }

  const headerLeftContentProps = {
    actionsContent: (
      <Button
        text='Refresh data'
        icon='refresh'
        size='small'
        contained='tertiary'
        loading={teamsStore.resyncingIntegrationTeams}
        disabled={!hasSelectedIntegrationTeam || teamsStore.resyncingIntegrationTeams}
        onClick={() => teamsStore.handleResyncIntegrationTeams(selectedTeamIdsForResync)}
        aria-label={'TeamsTableRefreshData'}
      />
    ),
  }

  return (
    <section className={styles.table}>
      <Table
        withCheckbox
        ariaLabel={'TeamsTable'}
        store={tableStore}
        columns={getColumns}
        rows={rows}
        loading={loading}
        onRowClick={onRowClick}
        headerLeftContentProps={headerLeftContentProps}
      />
    </section>
  )
})
